/**
 * Horizontal angular scale bar (issue #177): a strip of degree ticks across
 * the bottom of the view, centred on the camera's compass bearing, shown only
 * while the official-sheet framing preset (90° horizontal, see
 * camera/viewScale.ts) is active. It lets the turbine spread be read off in
 * degrees the way the developer's "90 Degree View" wireline sheets are
 * annotated.
 *
 * Presentation only: the bar reads the camera's bearing and the framing's
 * horizontal FOV and writes nothing back. No FOV, bearing or tick here ever
 * feeds a LOS result or its display (hard constraint #1), and nothing in this
 * file imports three.js or anything under raypath/.
 *
 * Split in the usual repo shape: `angularScaleBarTicks` is the pure geometry
 * (unit-tested in plain Node), `AngularScaleBar` is the thin DOM wrapper.
 *
 * Performance (repo rule: nothing on the render loop may allocate per frame or
 * read layout): the wrapper is called every frame but does nothing unless the
 * bearing has actually moved past `BEARING_EPSILON_DEG`, and never more often
 * than `UPDATE_INTERVAL_MS`. Tick nodes are pooled and re-used, surplus ones
 * hidden rather than destroyed, and the bar's width is passed in by main.ts's
 * resize handler — it is never measured from the DOM here.
 */

/** Ticks every 10°, the annotation interval on the official sheets. */
export const SCALE_BAR_TICK_STEP_DEG = 10;
/** Every third tick draws taller. */
export const SCALE_BAR_MAJOR_STEP_DEG = 30;
/**
 * Documented breakpoints for the "very small viewport" failure case in issue
 * #177. The annotation degrades in three steps as the bar narrows — labels
 * every 10°, then every 30° (below ~400 px for a 90° field, which is every
 * phone in portrait), then ticks with no labels at all, and below
 * `MIN_SCALE_BAR_WIDTH_PX` the bar is not drawn. The framing preset itself
 * stays fully usable at every step; only the annotation thins out.
 */
export const MIN_SCALE_BAR_WIDTH_PX = 120;
/** Space a degree label needs before its neighbours start colliding. */
export const MIN_LABEL_SPACING_PX = 44;
/** Refuse to draw a field so wide it would mean hundreds of ticks — a nonsense
 * input, not something to render (hard constraint #7). */
const MAX_TICKS = 64;

/** How far the camera must turn before the bar is worth redrawing. */
const BEARING_EPSILON_DEG = 0.1;
/** Redraw ceiling: 20 Hz is smooth to the eye and a twentieth of the DOM work
 * a per-frame redraw would do during a fast pan. */
const UPDATE_INTERVAL_MS = 50;

export interface ScaleBarTick {
  /** Compass bearing this tick marks, normalised to [0, 360). */
  bearingDeg: number;
  /** Position across the bar: 0 = left edge of the view, 1 = right edge. */
  fraction: number;
  /** Degree label, or null where labels are suppressed at this bar width. */
  label: string | null;
  /** Multiples of `SCALE_BAR_MAJOR_STEP_DEG` — drawn taller. */
  major: boolean;
}

function normaliseBearing(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * The degree ticks visible in a `horizontalFovDeg`-wide field centred on
 * `centreBearingDeg`, laid out across a bar `widthPx` wide.
 *
 * Returns an empty list — never a fabricated or partial scale — when any input
 * is missing/nonsense or the bar is too narrow to be legible.
 */
export function angularScaleBarTicks(
  centreBearingDeg: number,
  horizontalFovDeg: number,
  widthPx: number,
): ScaleBarTick[] {
  if (!Number.isFinite(centreBearingDeg) || !Number.isFinite(horizontalFovDeg) || !Number.isFinite(widthPx)) {
    return [];
  }
  if (horizontalFovDeg <= 0 || widthPx < MIN_SCALE_BAR_WIDTH_PX) return [];
  if (horizontalFovDeg / SCALE_BAR_TICK_STEP_DEG > MAX_TICKS) return [];

  // Labels thin out, then disappear, as the bar narrows; the ticks themselves
  // always stay so the scale remains readable against the horizon.
  const pxPerStep = (widthPx * SCALE_BAR_TICK_STEP_DEG) / horizontalFovDeg;
  const pxPerMajor = (widthPx * SCALE_BAR_MAJOR_STEP_DEG) / horizontalFovDeg;
  const labelStepDeg =
    pxPerStep >= MIN_LABEL_SPACING_PX
      ? SCALE_BAR_TICK_STEP_DEG
      : pxPerMajor >= MIN_LABEL_SPACING_PX
        ? SCALE_BAR_MAJOR_STEP_DEG
        : null;

  const leftDeg = centreBearingDeg - horizontalFovDeg / 2;
  const rightDeg = centreBearingDeg + horizontalFovDeg / 2;
  const first = Math.ceil(leftDeg / SCALE_BAR_TICK_STEP_DEG) * SCALE_BAR_TICK_STEP_DEG;

  const ticks: ScaleBarTick[] = [];
  for (let deg = first; deg <= rightDeg + 1e-9; deg += SCALE_BAR_TICK_STEP_DEG) {
    const bearingDeg = normaliseBearing(Math.round(deg));
    const major = bearingDeg % SCALE_BAR_MAJOR_STEP_DEG === 0;
    const labelled = labelStepDeg !== null && bearingDeg % labelStepDeg === 0;
    ticks.push({
      bearingDeg,
      fraction: (deg - leftDeg) / horizontalFovDeg,
      label: labelled ? `${bearingDeg}°` : null,
      major,
    });
  }
  return ticks;
}

interface TickNode {
  root: HTMLElement;
  mark: HTMLElement;
  label: HTMLElement;
  /** Last values written, so an unchanged tick costs no DOM write. */
  left: string;
  text: string;
  major: boolean;
  hidden: boolean;
}

/**
 * DOM wrapper for the scale bar. `update` is safe to call every frame; it
 * decides for itself whether anything is worth writing.
 */
export class AngularScaleBar {
  private readonly el: HTMLElement;
  private readonly nodes: TickNode[] = [];
  private visible = false;
  private lastBearingDeg = Number.NaN;
  private lastFovDeg = Number.NaN;
  private lastWidthPx = Number.NaN;
  private lastUpdateMs = Number.NEGATIVE_INFINITY;

  constructor(el: HTMLElement) {
    this.el = el;
    this.el.classList.add('hidden');
  }

  /**
   * @param active            whether the official-sheet preset is selected
   * @param centreBearingDeg  the camera's current compass bearing
   * @param horizontalFovDeg  the framing's horizontal FOV
   * @param widthPx           the bar's width, supplied by the resize handler
   * @param nowMs             a monotonic timestamp (the render loop's `now`)
   */
  update(
    active: boolean,
    centreBearingDeg: number,
    horizontalFovDeg: number,
    widthPx: number,
    nowMs: number,
  ): void {
    if (!active) {
      if (this.visible) this.hide();
      return;
    }

    const geometryChanged = horizontalFovDeg !== this.lastFovDeg || widthPx !== this.lastWidthPx;
    const turned =
      !Number.isFinite(this.lastBearingDeg) ||
      Math.abs(centreBearingDeg - this.lastBearingDeg) >= BEARING_EPSILON_DEG;
    // Becoming visible, or a resize/preset change, redraws at once; an ordinary
    // pan waits for the throttle so a fast turn cannot redraw every frame.
    const immediate = !this.visible || geometryChanged;
    if (!immediate) {
      if (!turned) return;
      if (nowMs - this.lastUpdateMs < UPDATE_INTERVAL_MS) return;
    }

    const ticks = angularScaleBarTicks(centreBearingDeg, horizontalFovDeg, widthPx);
    this.lastBearingDeg = centreBearingDeg;
    this.lastFovDeg = horizontalFovDeg;
    this.lastWidthPx = widthPx;
    this.lastUpdateMs = nowMs;

    // No legible scale at this width (or nonsense inputs): hide the bar rather
    // than show an empty or half-drawn one. The framing preset is unaffected.
    if (ticks.length === 0) {
      if (this.visible) this.hide();
      return;
    }

    this.render(ticks);
    if (!this.visible) {
      this.visible = true;
      this.el.classList.remove('hidden');
    }
  }

  private hide(): void {
    this.visible = false;
    this.el.classList.add('hidden');
    this.lastBearingDeg = Number.NaN;
    this.lastFovDeg = Number.NaN;
    this.lastWidthPx = Number.NaN;
  }

  private render(ticks: readonly ScaleBarTick[]): void {
    while (this.nodes.length < ticks.length) this.nodes.push(this.createTickNode());

    for (let i = 0; i < this.nodes.length; i += 1) {
      const node = this.nodes[i];
      const tick = ticks[i];
      if (!tick) {
        // Surplus pooled node: hidden, never left showing a stale degree.
        if (!node.hidden) {
          node.hidden = true;
          node.root.hidden = true;
        }
        continue;
      }
      if (node.hidden) {
        node.hidden = false;
        node.root.hidden = false;
      }
      const left = `${(tick.fraction * 100).toFixed(3)}%`;
      if (left !== node.left) {
        node.left = left;
        node.root.style.left = left;
      }
      const text = tick.label ?? '';
      if (text !== node.text) {
        node.text = text;
        node.label.textContent = text;
      }
      if (tick.major !== node.major) {
        node.major = tick.major;
        node.root.classList.toggle('major', tick.major);
      }
    }
  }

  private createTickNode(): TickNode {
    const root = document.createElement('span');
    root.className = 'scale-bar-tick';
    const mark = document.createElement('span');
    mark.className = 'scale-bar-mark';
    const label = document.createElement('span');
    label.className = 'scale-bar-label';
    root.append(mark, label);
    this.el.append(root);
    return { root, mark, label, left: '', text: '', major: false, hidden: false };
  }
}
